import { useState, type ReactNode } from 'react';
import { Table, Th, Tr, Td } from './ui';
import { fmtCurrencyWhole, fmtMonth, fmtPct } from '../lib/fmt';
import { lineCodeFamily, lineCodeLabel, type LineCodeFamily } from '../lib/fpaLineCodes';
import type { FpaStatement, StatementRow } from '../lib/api';

/**
 * The single income-statement renderer for the FP&A redesign — driven by the
 * unified `FpaStatement` contract, so the Plan P&L, Actuals, and Variance all
 * use ONE layout. A "primary" version supplies the headline value per cell; an
 * optional "compare" version adds a sign-adjusted Δ beneath it (green =
 * favourable to operating profit). Sections, subtotals, gross margin and
 * operating profit are derived here PER VERSION from the line families, so a
 * plan's stored margins are never compared against a differently-derived actual.
 * Each line expands (▸) into its per-department breakdown; cells are clickable
 * for lineage when onCellClick is given.
 */

const z = (n: number) => (n === 0 ? 0 : n);
const round2 = (n: number) => Math.round(n * 100) / 100;
const codeHint: React.CSSProperties = { fontSize: 10, color: 'var(--color-text-hint)', fontFamily: 'var(--font-mono, monospace)' };
const deptLabel = (d: string, names?: Record<string, string>) =>
  d === '__company__' ? '(company-wide)' : d === '__unmapped__' ? '(unmapped)' : (names?.[d] ?? d);

function delta(n: number): ReactNode {
  const color = n > 0 ? 'var(--color-green-text)' : n < 0 ? 'var(--color-red-text)' : 'var(--color-text-hint)';
  return <span style={{ color, fontSize: 10 }}>{n > 0 ? '+' : ''}{fmtCurrencyWhole(n)}</span>;
}

export function StatementView({ statement, primaryKey, compareKey, showTotalColumn = true, onCellClick, deptNames }: {
  statement: FpaStatement;
  /** Version whose value is the headline number (default: Actual if present, else the first version). */
  primaryKey?: string;
  /** Version to show Δ against (default: when primary is Actual, the first plan version; else none). null = force off. */
  compareKey?: string | null;
  showTotalColumn?: boolean;
  onCellClick?: (line: string, month: string, deptId?: string | null) => void;
  deptNames?: Record<string, string>;
}) {
  const { periods, versions, rows } = statement;
  const primary = primaryKey ?? versions.find((v) => v.key === 'actual')?.key ?? versions[0]?.key ?? '';
  const compare = compareKey === undefined
    ? (primary === 'actual' ? (versions.find((v) => v.key !== 'actual')?.key ?? null) : null)
    : compareKey;
  const hasCompare = !!compare;
  const primaryVer = versions.find((v) => v.key === primary);
  const compareVer = compare ? versions.find((v) => v.key === compare) : null;

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggle = (lc: string) => setExpanded((p) => { const n = new Set(p); if (n.has(lc)) n.delete(lc); else n.add(lc); return n; });

  const fam = (f: LineCodeFamily) => rows.filter((r) => lineCodeFamily(r.line_code) === f);
  const revLines = fam('revenue'); const varLines = fam('cost.variable'); const salLines = fam('cost.salary'); const fixLines = fam('cost.fixed');
  const otherLines = rows.filter((r) => lineCodeFamily(r.line_code) === null);

  const cv = (r: StatementRow, m: string, vk: string): number | undefined => r.cells.find((c) => c.period_month === m)?.values[vk];
  const famSum = (lines: StatementRow[], m: string, vk: string) => lines.reduce((s, r) => s + (cv(r, m, vk) ?? 0), 0);
  const revM = (m: string, vk: string) => famSum(revLines, m, vk);
  const cogsM = (m: string, vk: string) => famSum(varLines, m, vk) + famSum(salLines, m, vk);
  const fixM = (m: string, vk: string) => famSum(fixLines, m, vk);
  const grossM = (m: string, vk: string) => revM(m, vk) - cogsM(m, vk);
  const netM = (m: string, vk: string) => grossM(m, vk) - fixM(m, vk);
  const overMonths = (f: (m: string, vk: string) => number, vk: string) => periods.reduce((s, m) => s + f(m, vk), 0);

  const colSpan = periods.length + 1 + (showTotalColumn ? 1 : 0);

  // One value cell: primary value (sign-adjusted) + optional Δ vs compare.
  // primaryRaw/compareRaw are pre-sign amounts; undefined → that side is absent.
  const valTd = (
    key: string, primaryRaw: number | undefined, compareRaw: number | undefined, sign: 1 | -1,
    opts?: { line?: string; month?: string; dept?: string | null; bold?: boolean; small?: boolean },
  ): ReactNode => {
    const clickable = onCellClick && opts?.line && opts?.month;
    const valStr = primaryRaw == null ? null : fmtCurrencyWhole(z(sign * primaryRaw));
    const showDelta = hasCompare && (primaryRaw != null || compareRaw != null);
    const fs = opts?.small ? { fontSize: 11 } : undefined;
    return (
      <Td key={key} right bold={opts?.bold} muted={opts?.small} style={fs}>
        {valStr == null ? <span style={{ color: 'var(--color-text-hint)' }}>—</span>
          : clickable ? <span style={{ cursor: 'pointer', textDecoration: 'underline dotted' }} title="View lineage" onClick={() => onCellClick!(opts!.line!, opts!.month!, opts?.dept ?? null)}>{valStr}</span>
          : valStr}
        {showDelta && <div>{delta(round2(sign * ((primaryRaw ?? 0) - (compareRaw ?? 0))))}</div>}
      </Td>
    );
  };

  // A data line, expandable to its departments.
  function lineRow(r: StatementRow, sign: 1 | -1): ReactNode[] {
    const canExpand = r.by_department.length > 0;
    const isOpen = expanded.has(r.line_code);
    const nodes: ReactNode[] = [
      <Tr key={r.line_code}>
        <Td style={{ paddingLeft: canExpand ? 10 : 28 }}>
          {canExpand && <span onClick={() => toggle(r.line_code)} title={isOpen ? 'Collapse departments' : 'Break down by department'} style={{ cursor: 'pointer', userSelect: 'none', display: 'inline-block', width: 16, color: 'var(--color-text-muted)' }}>{isOpen ? '▾' : '▸'}</span>}
          {lineCodeLabel(r.line_code)} <span style={codeHint}>{r.line_code}</span>
        </Td>
        {periods.map((m) => valTd(m, cv(r, m, primary), compare ? cv(r, m, compare) : undefined, sign, { line: r.line_code, month: m }))}
        {showTotalColumn && valTd('__t', periods.reduce((s, m) => s + (cv(r, m, primary) ?? 0), 0), compare ? periods.reduce((s, m) => s + (cv(r, m, compare) ?? 0), 0) : undefined, sign, { bold: true })}
      </Tr>,
    ];
    if (canExpand && isOpen) {
      for (const d of r.by_department) {
        const dcv = (m: string, vk: string) => d.cells.find((c) => c.period_month === m)?.values[vk];
        nodes.push(
          <Tr key={`${r.line_code}__${d.department_id}`}>
            <Td muted style={{ paddingLeft: 44, fontSize: 11 }}>{deptLabel(d.department_id, deptNames)}</Td>
            {periods.map((m) => valTd(m, dcv(m, primary), compare ? dcv(m, compare) : undefined, sign, { line: r.line_code, month: m, dept: d.department_id, small: true }))}
            {showTotalColumn && valTd('__t', periods.reduce((s, m) => s + (dcv(m, primary) ?? 0), 0), compare ? periods.reduce((s, m) => s + (dcv(m, compare) ?? 0), 0) : undefined, sign, { small: true })}
          </Tr>,
        );
      }
    }
    return nodes;
  }

  // A derived subtotal / gm / op row (computed per version; never clickable).
  function aggRow(key: string, label: string, fn: (m: string, vk: string) => number, sign: 1 | -1, opts?: { accent?: 'green' | 'border'; emphatic?: boolean }): ReactNode {
    const bg = opts?.accent === 'green' ? 'var(--color-green-bg, rgba(34,197,94,.08))' : opts?.accent === 'border' ? 'var(--color-border)' : undefined;
    const w = opts?.emphatic ? { fontWeight: 600 as const } : undefined;
    return (
      <Tr key={key} style={bg ? { background: bg } : undefined}>
        <Td bold style={w}>{label}</Td>
        {periods.map((m) => valTd(m, fn(m, primary), compare ? fn(m, compare) : undefined, sign, { bold: true }))}
        {showTotalColumn && valTd('__t', overMonths(fn, primary), compare ? overMonths(fn, compare) : undefined, sign, { bold: true })}
      </Tr>
    );
  }

  function marginRow(key: string, label: string, fn: (m: string, vk: string) => number): ReactNode {
    const totRev = overMonths(revM, primary);
    return (
      <Tr key={key}>
        <Td muted style={{ paddingLeft: 28, fontSize: 11 }}>{label}</Td>
        {periods.map((m) => { const r = revM(m, primary); return <Td key={m} right muted style={{ fontSize: 11 }}>{r === 0 ? '—' : fmtPct(fn(m, primary) / r)}</Td>; })}
        {showTotalColumn && <Td right muted style={{ fontSize: 11 }}>{totRev === 0 ? '—' : fmtPct(overMonths(fn, primary) / totRev)}</Td>}
      </Tr>
    );
  }

  const sectionHeader = (label: string): ReactNode => (
    <tr key={`sec-${label}`}><td colSpan={colSpan} style={{ padding: '10px 16px 4px', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--color-text-muted)' }}>{label}</td></tr>
  );

  const body: ReactNode[] = [];
  body.push(sectionHeader('Revenue'));
  revLines.forEach((r) => body.push(...lineRow(r, 1)));
  body.push(aggRow('rev-sub', 'Total revenue', revM, 1));
  if (varLines.length + salLines.length > 0) {
    body.push(sectionHeader('Cost of services'));
    varLines.forEach((r) => body.push(...lineRow(r, -1)));
    salLines.forEach((r) => body.push(...lineRow(r, -1)));
    body.push(aggRow('cogs-sub', 'Total cost of services', (m, vk) => -cogsM(m, vk), 1));
  }
  body.push(aggRow('gross', 'Gross profit', grossM, 1, { accent: 'green' }));
  body.push(marginRow('gross-m', 'Gross margin', grossM));
  if (fixLines.length > 0) {
    body.push(sectionHeader('Operating expenses'));
    fixLines.forEach((r) => body.push(...lineRow(r, -1)));
    body.push(aggRow('fix-sub', 'Total operating expenses', (m, vk) => -fixM(m, vk), 1));
  }
  body.push(aggRow('op', 'Operating profit', netM, 1, { accent: 'border', emphatic: true }));
  body.push(marginRow('op-m', 'Operating margin', netM));
  if (otherLines.length > 0) {
    body.push(sectionHeader('Other'));
    otherLines.forEach((r) => body.push(...lineRow(r, 1)));
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', padding: '6px 16px', fontSize: 11, color: 'var(--color-text-muted)', flexWrap: 'wrap' }}>
        <span>Showing <strong style={{ color: 'var(--color-text)' }}>{primaryVer?.label ?? primary}</strong></span>
        {compareVer && <span>· Δ vs <strong style={{ color: 'var(--color-text)' }}>{compareVer.label}</strong> <span style={{ color: 'var(--color-text-hint)' }}>(green = favourable)</span></span>}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <Table>
          <thead>
            <tr>
              <Th sticky>Line</Th>
              {periods.map((m) => <Th key={m} right>{fmtMonth(m)}</Th>)}
              {showTotalColumn && <Th right>Total</Th>}
            </tr>
          </thead>
          <tbody>{body}</tbody>
        </Table>
      </div>
    </div>
  );
}
